import { Ga4Event, OnboardingTrackingStep, Touchpoint } from '@/constants/enum';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

interface ITouchpoint {
  [Touchpoint.PricingPlan]: boolean;
  [Touchpoint.HomeTopBanner]: boolean;
  [Touchpoint.ChecklistGeneralRuleLimit]: boolean;
  [Touchpoint.ChecklistShowInCart]: boolean;
  [Touchpoint.ChecklistSpecificRuleLimit]: boolean;
  [Touchpoint.ChecklistAutoDetectLocation]: boolean;
  [Touchpoint.SettingsTopBanner]: boolean;
  [Touchpoint.SetupZipcodeLimit]: boolean;
  [Touchpoint.SetupCountryLimit]: boolean;
  [Touchpoint.SetupStateRule]: boolean;
  [Touchpoint.SetupAdditionalTopBanner]: boolean;
  [Touchpoint.SetupAppliedItems]: boolean;
  [Touchpoint.SetupShowInCart]: boolean;
  [Touchpoint.SetupSpecificRuleItemLimit]: boolean;
  [Touchpoint.SetupSpecificAdditionalTopBanner]: boolean;
  [Touchpoint.SetupSpecificShowInCart]: boolean;
  [Touchpoint.AppearanceChangeIcon]: boolean;
  [Touchpoint.AppearanceVisualEditor]: boolean;
  [Touchpoint.CustomCSS]: boolean;
  [Touchpoint.SetupOutOfStock]: boolean;
  [Touchpoint.UpgradeETACheckout]: boolean;
}

interface IGa4Event {
  [Ga4Event.PricingPlanImpression]: boolean;
  [Ga4Event.ChoosePlan]: boolean;
  [Ga4Event.ChargeDone]: boolean;
  [Ga4Event.Uninstall]: boolean;
  [Ga4Event.SynctrackImpression]: boolean;
  [Ga4Event.SynctrackClick]: boolean;
  [Ga4Event.BlockifyImpression]: boolean;
  [Ga4Event.BlockifyClick]: boolean;
  [Ga4Event.OrderTrackingImpression]: boolean;
  [Ga4Event.OrderTrackingClick]: boolean;
  [Ga4Event.ReturnsImpression]: boolean;
  [Ga4Event.ReturnsClick]: boolean;
  [Ga4Event.ParetoClick]: boolean;
  [Ga4Event.ParetoImpression]: boolean;
}

interface IOnboarding {
  [OnboardingTrackingStep.Step1]: boolean;
  [OnboardingTrackingStep.Step2]: boolean;
  [OnboardingTrackingStep.Step3]: boolean;
  [OnboardingTrackingStep.Step4]: boolean;
  [OnboardingTrackingStep.Step5]: boolean;
  [OnboardingTrackingStep.Finish]: boolean;
}

interface SessionState {
  touchpoint: ITouchpoint;
  ga4Event: IGa4Event;
  onboardingStep: IOnboarding;
  banner: {
    isVisibleBannerLimitInHome: boolean;
    isVisibleBannerLimitInTranslation: boolean;
    isVisibleBannerLimitInRuleConfig: boolean;
    isVisibleBannerLimitCheckout: boolean;
    isShowPopupTrialCampaign: boolean;
    isVisibleSetupChecklist: boolean;
    isShowBannerConditionRule: boolean;
    isShowBannerConditionCheckout: boolean;
  };
}

const initialState: SessionState = {
  touchpoint: {
    [Touchpoint.PricingPlan]: false,
    [Touchpoint.HomeTopBanner]: false,
    [Touchpoint.ChecklistGeneralRuleLimit]: false,
    [Touchpoint.ChecklistShowInCart]: false,
    [Touchpoint.ChecklistSpecificRuleLimit]: false,
    [Touchpoint.ChecklistAutoDetectLocation]: false,
    [Touchpoint.SettingsTopBanner]: false,
    [Touchpoint.SetupZipcodeLimit]: false,
    [Touchpoint.SetupCountryLimit]: false,
    [Touchpoint.SetupStateRule]: false,
    [Touchpoint.SetupAdditionalTopBanner]: false,
    [Touchpoint.SetupAppliedItems]: false,
    [Touchpoint.SetupShowInCart]: false,
    [Touchpoint.SetupSpecificRuleItemLimit]: false,
    [Touchpoint.SetupSpecificAdditionalTopBanner]: false,
    [Touchpoint.SetupSpecificShowInCart]: false,
    [Touchpoint.AppearanceChangeIcon]: false,
    [Touchpoint.AppearanceVisualEditor]: false,
    [Touchpoint.CustomCSS]: false,
    [Touchpoint.SetupOutOfStock]: false,
    [Touchpoint.UpgradeETACheckout]: false,
  },
  ga4Event: {
    [Ga4Event.PricingPlanImpression]: false,
    [Ga4Event.ChoosePlan]: false,
    [Ga4Event.ChargeDone]: false,
    [Ga4Event.Uninstall]: false,
    [Ga4Event.SynctrackImpression]: false,
    [Ga4Event.SynctrackClick]: false,
    [Ga4Event.BlockifyImpression]: false,
    [Ga4Event.BlockifyClick]: false,
    [Ga4Event.OrderTrackingImpression]: false,
    [Ga4Event.OrderTrackingClick]: false,
    [Ga4Event.ReturnsImpression]: false,
    [Ga4Event.ReturnsClick]: false,
    [Ga4Event.ParetoClick]: false,
    [Ga4Event.ParetoImpression]: false,
  },
  onboardingStep: {
    [OnboardingTrackingStep.Step1]: false,
    [OnboardingTrackingStep.Step2]: false,
    [OnboardingTrackingStep.Step3]: false,
    [OnboardingTrackingStep.Step4]: false,
    [OnboardingTrackingStep.Step5]: false,
    [OnboardingTrackingStep.Finish]: false,
  },
  banner: {
    isVisibleBannerLimitInHome: true,
    isVisibleBannerLimitInTranslation: true,
    isVisibleBannerLimitInRuleConfig: true,
    isVisibleBannerLimitCheckout: true,
    isShowPopupTrialCampaign: true,
    isVisibleSetupChecklist: true,
    isShowBannerConditionRule: true,
    isShowBannerConditionCheckout: true,
  },
};

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    handleTouchpoint: (state, action: PayloadAction<Touchpoint>) => {
      state.touchpoint[action.payload] = true;
    },
    handleGa4Event: (state, action: PayloadAction<Ga4Event>) => {
      state.ga4Event[action.payload] = true;
    },
    handleOnboardingStep: (state, action: PayloadAction<OnboardingTrackingStep>) => {
      state.onboardingStep[action.payload] = true;
    },
    handleBanner: (state, action: PayloadAction<{ key: keyof (typeof initialState)['banner']; value: boolean }>) => {
      state.banner[action.payload.key] = action.payload.value;
    },
  },
});

export const touchpointSelector = createSelector(
  (state: RootState) => state.session,
  (state) => state.touchpoint,
);
export const ga4EventSelector = createSelector(
  (state: RootState) => state.session,
  (state) => state.ga4Event,
);
export const onboardingStepSelector = createSelector(
  (state: RootState) => state.session,
  (state) => state.onboardingStep,
);
export const isShowBannerSelector = createSelector(
  (state: RootState) => state.session,
  (state) => state.banner,
);

export default sessionSlice;
